import { Label, Text } from "north.js";
import React, { useMemo } from "react";
import styled from "styled-components";

import { HardwareSku } from "src/api/types.generated";
import { Spinner } from "src/shared/components";
import Modal from "src/shared/components/Modal";
import { formatHardwareRequestStatus } from "src/shared/utils/hardware/formatHardwareRequestStatus";
import { mapStatusToLabelColor } from "src/shared/utils/hardware/mapStatusToLabelColor";
import globalConstants from "src/theme/globalConstants";

import { useGetUserHardwareRequestsQuery } from "../../../graphql/getUserHardwareRequests.generated";

interface OtherRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  userName: string;
  currentRequestId?: number;
  items: Pick<HardwareSku, "code" | "name">[];
}

const OtherRequestsModal: React.FC<OtherRequestsModalProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  currentRequestId,
  items,
}) => {
  const { data, loading } = useGetUserHardwareRequestsQuery({
    variables: {
      where: {
        user_id: { equals: userId },
      },
    },
    skip: !isOpen,
  });

  const allRequests = useMemo(() => {
    const requests = data?.hardwareRequests ?? [];
    return requests
      .slice()
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
  }, [data]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${userName}'s Requests`}
      width={window.innerWidth <= 766 ? "100%" : "780px"}
      scroll
    >
      {loading ? (
        <Spinner text="Loading requests..." />
      ) : allRequests.length === 0 ? (
        <Text
          type="body1"
          style={{ color: globalConstants?.color?.textSecondary as string }}
        >
          This user has no hardware requests.
        </Text>
      ) : (
        <RequestList>
          {allRequests.map((request) => (
            <RequestRow key={request.id}>
              <RequestRowHeader>
                <RequestRowHeaderLeft>
                  <Text type="body2" style={{ fontWeight: 600 }}>
                    Request #{request.id}
                  </Text>
                  {request.id === currentRequestId && (
                    <Label size="sm" color="secondary">
                      Current
                    </Label>
                  )}
                </RequestRowHeaderLeft>
                <Label size="sm" color={mapStatusToLabelColor(request.status)}>
                  {formatHardwareRequestStatus(request.status)}
                </Label>
              </RequestRowHeader>
              <Text
                type="body2"
                style={{
                  color: globalConstants?.color?.textSecondary as string,
                }}
              >
                {new Date(request.created_at).toLocaleString()}
              </Text>
              <ItemsSummary>
                {request.hardware_item_requests?.map((itemRequest) => {
                  const sku = items.find(
                    (item) => item.code === itemRequest.HardwareItem.sku_code
                  );
                  return (
                    <Text type="body2" key={itemRequest.id}>
                      {itemRequest.quantity}x{" "}
                      {sku?.name ?? itemRequest.HardwareItem.sku_code}
                    </Text>
                  );
                }) ?? null}
              </ItemsSummary>
            </RequestRow>
          ))}
        </RequestList>
      )}
    </Modal>
  );
};

export default OtherRequestsModal;

const RequestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;
`;

const RequestRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.textLight};
`;

const RequestRowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RequestRowHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemsSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
`;
